import { Button, Input, Modal } from '@ohear/ui'
/**
 * Phone verification modal — 6-digit OTP input with countdown + resend.
 *
 * V1 ships against the dev-stub backend (the API logs an OTP and accepts
 * `000000`). The modal itself is production-shaped — when Twilio is
 * wired in the API, the UI doesn't change.
 *
 * Behaviour:
 *   - On open, calls `onSendCode()` once. Shows a `expires_in`-second
 *     countdown after which the resend button re-enables.
 *   - The 6-digit input auto-focuses and is single-input
 *     (no per-digit boxes) for simpler accessibility — paste support
 *     is the main pragmatic win.
 *   - Submit calls `onVerify(code)`; the parent surfaces success/error
 *     via toast and closes the modal on success.
 */
import { type ChangeEvent, useEffect, useRef, useState } from 'react'

export interface PhoneVerifyModalProps {
  open: boolean
  /** E.164 phone the code was sent to (shown in the body for confidence). */
  phone: string | null
  /** Called once on open to send the initial code. Should resolve with TTL. */
  onSendCode: () => Promise<{ expires_in: number }> | { expires_in: number }
  /** Called when the user submits the code. Throws on wrong code. */
  onVerify: (code: string) => Promise<void>
  onClose: () => void
  /** Optional dev-mode hint shown under the input. */
  devHint?: string
}

export function PhoneVerifyModal(props: PhoneVerifyModalProps) {
  const { open, phone, onSendCode, onVerify, onClose, devHint } = props
  const [code, setCode] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sentOnceRef = useRef(false)

  // Send the initial code once per open. Reset state on close so a re-open
  // starts fresh (no stale code, no leftover error).
  useEffect(() => {
    if (!open) {
      sentOnceRef.current = false
      setCode('')
      setError(null)
      setSecondsLeft(0)
      return
    }
    if (sentOnceRef.current) return
    sentOnceRef.current = true
    Promise.resolve(onSendCode())
      .then((r) => setSecondsLeft(r.expires_in))
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not send code'))
  }, [open, onSendCode])

  // Countdown tick.
  useEffect(() => {
    if (secondsLeft <= 0) return
    const t = setTimeout(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000)
    return () => clearTimeout(t)
  }, [secondsLeft])

  const onCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Allow only digits; cap at 6.
    const digits = e.target.value.replace(/\D+/g, '').slice(0, 6)
    setCode(digits)
    setError(null)
  }

  const handleSubmit = async () => {
    if (code.length !== 6 || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await onVerify(code)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That code did not match')
    } finally {
      setSubmitting(false)
    }
  }

  const handleResend = async () => {
    if (secondsLeft > 0 || resending) return
    setResending(true)
    setError(null)
    try {
      const r = await Promise.resolve(onSendCode())
      setSecondsLeft(r.expires_in)
      setCode('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not resend code')
    } finally {
      setResending(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Verify your phone"
      size="sm"
      footer={
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => void handleResend()}
            disabled={secondsLeft > 0 || resending}
            className="text-[13px] text-[var(--accent,#3b82f6)] disabled:text-[var(--text-3)] disabled:cursor-not-allowed bg-transparent border-0 p-0"
          >
            {secondsLeft > 0 ? `Resend in ${secondsLeft}s` : 'Resend code'}
          </button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleSubmit()}
              disabled={code.length !== 6}
              loading={submitting}
            >
              Verify
            </Button>
          </div>
        </div>
      }
    >
      <p className="text-[13px] text-[var(--text-2)] m-0">
        We just sent a 6-digit code to{' '}
        <span className="font-medium text-[var(--text)]">{phone ?? 'your phone'}</span>. Enter it
        below to confirm this number is yours.
      </p>
      <div className="mt-3 flex flex-col gap-1">
        <label htmlFor="phone-otp-input" className="text-[12px] font-medium text-[var(--text)]">
          Verification code
        </label>
        <Input
          id="phone-otp-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={onCodeChange}
          placeholder="000000"
          autoFocus
        />
        {devHint ? <p className="text-[12px] text-[var(--text-3)] m-0">{devHint}</p> : null}
        {error ? (
          <p role="alert" className="text-[12px] text-[var(--danger-text,#a8071a)] m-0">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}
